import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import Swal, { SweetAlertOptions, SweetAlertResult } from 'sweetalert2';
import * as moment from 'moment';
import 'moment/locale/it';
import { AddressLookupResult } from '../../../../services/address-lookup/address-lookup.service';
import { loadItalianProvinces, Province, residenceFromLookup } from '../../../../resources/provinces';
import {
    type ConvocatedUserPreview,
    type ConvocationRowItem,
} from '../mister-convocation-shared';
import { Category } from '../../../../models/category';
import { AthleteSeason } from '../../../../models/athleteSeason';
import { CompanyDocument } from '../../../../models/CompanyDocument';
import { AthleteService } from '../../../../services/athlete/athlete.service';
import { CategoryService } from '../../../../services/category/category.service';
import {
    ConvocationApiPayload,
    ConvocationService,
} from '../../../../services/convocation/convocation.service';
import { SeasonService } from '../../../../services/season/season.service';
import { CompanyService } from '../../../../services/company/company.service';
import { UtilsService } from '../../../../services/utils';
import { TableComponent } from '../../../ui/table/table.component';

@Component({
    selector: 'app-mister-convocation-form',
    templateUrl: './mister-convocation-form.component.html',
    styleUrls: ['./mister-convocation-form.component.scss'],
})
export class MisterConvocationFormComponent implements OnChanges {
    @ViewChild('athletesTable') athletesTable?: TableComponent;

    /** Pass the convocation to edit, or null/undefined to create a new one. */
    @Input() convocation?: ConvocationRowItem | null;
    @Input() isOpen = false;
    /** Category preselected when creating (e.g. opened from a category page). */
    @Input() defaultCategoryId?: string;

    @Output() closed = new EventEmitter<void>();
    @Output() saved = new EventEmitter<ConvocationRowItem>();

    readonly statusOptions = ['Pubblicata', 'Bozza', 'Chiusa', 'Annullata'];

    form: Partial<ConvocationRowItem> = {};
    invitedUsers: ConvocatedUserPreview[] = [];
    saveInProgress = false;

    primaryDocumentExists = false;
    readonly now = moment();

    /** Categorie società (stessa logica foglia/radice di altre schermate). */
    categorySelectOptions: { id: string; label: string }[] = [];
    categoriesLoading = false;

    /** Stagione corrente usata anche per filtrare gli atleti (come in `getAthlete` API). */
    private inviteFormSeasonId: string | undefined;

    readonly companyId = localStorage.getItem('wisper_companyId') ?? '';

    /** Quick picks for the date and the time of the convocation. */
    readonly timePresets = ['10:30', '15:00', '17:30', '18:30', '20:00'];
    mapPreviewUrl: SafeResourceUrl | null = null;
    private mapPreviewQuery = '';
    private pickedCoords: { lat: string; lon: string } | null = null;
    private autoMapLink = '';
    private provinces: Province[] = [];

    constructor(
        private athleteService: AthleteService,
        private categoryService: CategoryService,
        private seasonService: SeasonService,
        private convocationService: ConvocationService,
        private companyService: CompanyService,
        public utils: UtilsService,
        private sanitizer: DomSanitizer,
    ) {
        void loadItalianProvinces().then((provinces) => (this.provinces = provinces));
    }

    // ---- Date & time ----

    get datePart(): string {
        return (this.form.date ?? '').slice(0, 10);
    }

    set datePart(value: string) {
        this.form = { ...this.form, date: `${value || moment().format('YYYY-MM-DD')}T${this.timePart || '18:00'}` };
    }

    get timePart(): string {
        return (this.form.date ?? '').slice(11, 16);
    }

    set timePart(value: string) {
        this.form = { ...this.form, date: `${this.datePart || moment().format('YYYY-MM-DD')}T${value || '18:00'}` };
    }

    get dayPresets(): { label: string; date: string }[] {
        const today = moment().startOf('day');
        const next = (isoWeekday: number) => {
            const day = today.clone().isoWeekday(isoWeekday);
            return day.isAfter(today) ? day : day.add(1, 'week');
        };
        return [
            { label: 'Oggi', date: today.format('YYYY-MM-DD') },
            { label: 'Domani', date: today.clone().add(1, 'day').format('YYYY-MM-DD') },
            { label: 'Sabato', date: next(6).format('YYYY-MM-DD') },
            { label: 'Domenica', date: next(7).format('YYYY-MM-DD') },
        ];
    }

    get dateSummary(): string {
        const date = moment(this.form.date, 'YYYY-MM-DDTHH:mm', true);
        if (!date.isValid()) {
            return '';
        }
        const label = date.clone().locale('it').format('dddd D MMMM YYYY [· ore] HH:mm');
        return `${label.charAt(0).toUpperCase()}${label.slice(1)} · ${date.clone().locale('it').fromNow()}`;
    }

    get isDateInPast(): boolean {
        const date = moment(this.form.date, 'YYYY-MM-DDTHH:mm', true);
        return date.isValid() && date.isBefore(moment());
    }

    // ---- Place & map ----

    onAddressSelected(result: AddressLookupResult): void {
        const place = residenceFromLookup(result, this.provinces);
        const placeName = ((result as any).name as string | undefined)?.trim();
        this.form = {
            ...this.form,
            address: place.address || this.form.address,
            city: place.city ?? this.form.city,
            province: place.province ?? this.form.province,
            // A named result (e.g. a sports centre) is a good default for the place name.
            location: this.form.location?.trim() ? this.form.location : (placeName && placeName !== place.address ? placeName : this.form.location),
        };
        clearTimeout(this.mapPreviewTimer);
        this.pickedCoords = { lat: result.lat, lon: result.lon };
        const link = `https://www.google.com/maps/search/?api=1&query=${result.lat},${result.lon}`;
        // Keep a link the user typed; replace only empty or auto-generated ones.
        if (!this.form.linkPosition?.trim() || this.form.linkPosition === this.autoMapLink) {
            this.form = { ...this.form, linkPosition: link };
            this.autoMapLink = link;
        }
        this.refreshMapPreview();
    }

    private mapPreviewTimer?: ReturnType<typeof setTimeout>;

    onPlaceTextChange(): void {
        // Typed edits invalidate coordinates from an earlier suggestion.
        this.pickedCoords = null;
        clearTimeout(this.mapPreviewTimer);
        this.mapPreviewTimer = setTimeout(() => this.refreshMapPreview(), 900);
    }

    refreshMapPreview(): void {
        const query = this.pickedCoords
            ? `${this.pickedCoords.lat},${this.pickedCoords.lon}`
            : [this.form.address, this.form.city, this.form.province]
                .map((part) => (part ?? '').trim())
                .filter((part) => part && part !== '—')
                .join(', ');
        if (query === this.mapPreviewQuery) {
            return;
        }
        this.mapPreviewQuery = query;
        // Rebuilt only when the query changes, so the iframe doesn't reload on every change detection.
        this.mapPreviewUrl = query.length >= 5
            ? this.sanitizer.bypassSecurityTrustResourceUrl(
                `https://maps.google.com/maps?q=${encodeURIComponent(query)}&z=16&output=embed`,
            )
            : null;
    }

    get mapsSearchLink(): string {
        return this.mapPreviewQuery
            ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.mapPreviewQuery)}`
            : '';
    }

    useMapsLink(): void {
        if (this.mapsSearchLink) {
            this.form = { ...this.form, linkPosition: this.mapsSearchLink };
            this.autoMapLink = this.mapsSearchLink;
        }
    }

    get isEditMode(): boolean {
        return !!this.convocation;
    }

    get drawerTitle(): string {
        return this.isEditMode ? 'Modifica convocazione' : 'Nuova convocazione';
    }

    isInvited(athleteId?: string): boolean {
        return !!athleteId && this.invitedUsers.some((u) => u.id === athleteId);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['isOpen']?.currentValue === true) {
            void this.initForm();
        }
    }

    onCategoryChange(): void {
        this.invitedUsers = [];
        void this.loadAthletesForCategory();
    }

    toggleInvited(row: AthleteSeason): void {
        const id = row.athlete?.id;
        if (!id) {
            return;
        }
        if (this.isInvited(id)) {
            this.removeUser(id);
        } else {
            this.addUser(this.mapAthleteSeasonToPreview(row));
        }
    }

    isMembershipActive(row: AthleteSeason): boolean {
        const memberships = row.athlete?.memberships ?? [];
        if (!memberships.length) {
            return false;
        }
        return moment(
            memberships[0].badgeExpiration,
            'YYYY-MM-DD HH:mm:ss',
        ).isAfter(this.now);
    }

    membershipLabel(row: AthleteSeason): string {
        return this.isMembershipActive(row) ? 'Tesserato' : 'Non tesserato';
    }

    membershipExpiryLabel(row: AthleteSeason): string | null {
        if (!this.isMembershipActive(row)) {
            return null;
        }
        const exp = row.athlete!.memberships[0].badgeExpiration;
        return moment(exp, 'YYYY-MM-DD HH:mm:ss').format('DD-MM-YYYY');
    }

    private async initForm(): Promise<void> {
        this.categorySelectOptions = [];
        void this.loadPrimaryDocumentCheck();

        if (this.convocation) {
            this.form = {
                ...this.convocation,
                date: this.normalizeDateTimeForInput(this.convocation.date),
            };
        } else {
            this.form = {
                id: this.generateId(),
                title: '',
                description: '',
                status: 'Bozza',
                date: this.todayIso(),
                location: '',
                address: '',
                city: '',
                province: '',
                linkPosition: '',
                categoryName: '',
                categoryId: this.defaultCategoryId ?? '',
            };
        }

        this.pickedCoords = null;
        this.autoMapLink = '';
        this.mapPreviewQuery = '';
        this.mapPreviewUrl = null;
        this.refreshMapPreview();

        await this.loadCategorySelectOptions();

        if (this.convocation?.categoryName && !this.form.categoryId) {
            const match = this.categorySelectOptions.find(
                (o) => o.label === this.convocation!.categoryName,
            );
            if (match) {
                this.form = { ...this.form, categoryId: match.id };
            }
        }

        this.invitedUsers = [];
        await this.loadAthletesForCategory();

        if (this.convocation?.users?.length) {
            await this.restoreInvitedFromConvocation(this.convocation.users);
        }
    }

    private async restoreInvitedFromConvocation(
        userIds: string[],
    ): Promise<void> {
        const idSet = new Set(userIds.filter(Boolean));
        const fromTable = (this.athletesTable?.rows ?? [])
            .map((row: AthleteSeason) => this.mapAthleteSeasonToPreview(row))
            .filter((u) => idSet.has(u.id));
        const missing = [...idSet].filter(
            (id) => !fromTable.some((u) => u.id === id),
        );
        const extras: ConvocatedUserPreview[] = [];
        for (const mid of missing) {
            const a = await this.athleteService.getAthleteById(mid);
            if (a?.id) {
                extras.push(this.mapAthleteToPreview(a));
            }
        }
        this.invitedUsers = [...fromTable, ...extras].sort((a, b) =>
            a.fullName.localeCompare(b.fullName, 'it'),
        );
    }

    private async loadPrimaryDocumentCheck(): Promise<void> {
        this.primaryDocumentExists = false;
        if (!this.companyId) {
            return;
        }
        try {
            const documents = await this.companyService.getCompanyDocuments(
                this.companyId,
            );
            this.primaryDocumentExists = documents.some(
                (document: CompanyDocument) => document.primaryDocument === 1,
            );
        } catch {
            this.primaryDocumentExists = false;
        }
    }

    private async loadCategorySelectOptions(): Promise<void> {
        if (!this.companyId) {
            this.categorySelectOptions = [];
            this.inviteFormSeasonId = undefined;
            return;
        }
        this.categoriesLoading = true;
        try {
            const seasonsRes = await this.seasonService.getSeasons(
                this.companyId,
                { isCurrent: true },
            );
            const currentSeasonId = seasonsRes?.data?.[0]?.id as
                | string
                | undefined;
            this.inviteFormSeasonId = currentSeasonId;
            const all = await this.categoryService.getCategories(
                this.companyId,
                false,
                currentSeasonId ? { seasonId: currentSeasonId } : undefined,
            );
            const leaves = all
                .filter((c: Category) => !!c.id && c.parentId != null)
                .map((c: Category) => ({
                    id: c.id as string,
                    label: c.parent
                        ? `${c.parent.name} - ${c.name}`
                        : c.name,
                }));
            const roots = all
                .filter((c: Category) => !!c.id && !c.parentId)
                .map((c: Category) => ({
                    id: c.id as string,
                    label: c.name,
                }));
            this.categorySelectOptions = (
                leaves.length > 0 ? leaves : roots
            ).sort((a, b) => a.label.localeCompare(b.label, 'it'));
        } catch {
            this.categorySelectOptions = [];
        } finally {
            this.categoriesLoading = false;
        }
    }

    private async loadAthletesForCategory(): Promise<void> {
        const categoryId = (this.form.categoryId ?? '').trim();
        if (!categoryId) {
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 0));
        if (!this.athletesTable) {
            return;
        }
        await this.athletesTable.loadData(
            '/athletesSeasons',
            { categoryId },
            (item: any) => new AthleteSeason(item),
            true,
        );
    }

    private mapAthleteSeasonToPreview(row: AthleteSeason): ConvocatedUserPreview {
        const a = row.athlete;
        const id = String(a?.id ?? '').trim();
        const first = (a?.firstName ?? '').trim();
        const last = (a?.lastName ?? '').trim();
        const fullName =
            [first, last].filter(Boolean).join(' ').trim() || '—';
        const roleLabel = (row.status as string)?.trim() || undefined;
        return { id, fullName, roleLabel };
    }

    private mapAthleteToPreview(a: any): ConvocatedUserPreview {
        const id = String(a?.id ?? '').trim();
        const first = (a?.firstName ?? '').trim();
        const last = (a?.lastName ?? '').trim();
        const fullName =
            [first, last].filter(Boolean).join(' ').trim() ||
            (a?.fullName as string)?.trim() ||
            '—';
        const roleLabel =
            (a?.note as string)?.trim() ||
            (a?.status as string)?.trim() ||
            undefined;
        return { id, fullName, roleLabel };
    }

    addUser(user: ConvocatedUserPreview): void {
        if (!user.id || this.isInvited(user.id)) {
            return;
        }
        this.invitedUsers = [...this.invitedUsers, user];
    }

    removeUser(userId: string): void {
        this.invitedUsers = this.invitedUsers.filter((u) => u.id !== userId);
    }

    /** SweetAlert è montato su `body`; alza lo z-index sopra il drawer (1040). */
    private async drawerSwal(
        opts: SweetAlertOptions,
    ): Promise<SweetAlertResult> {
        return Swal.fire({
            ...opts,
            didOpen: (popup: HTMLElement) => {
                opts.didOpen?.(popup);
                const el = document.querySelector(
                    '.swal2-container',
                ) as HTMLElement | null;
                if (el) {
                    el.style.zIndex = '200000';
                }
            },
        });
    }

    async onSave(ev?: Event): Promise<void> {
        ev?.preventDefault();
        ev?.stopPropagation();

        if (this.saveInProgress) {
            return;
        }

        const userId = localStorage.getItem('wisper_userId') ?? '';
        if (!userId) {
            await this.drawerSwal({
                icon: 'error',
                title: 'Sessione non valida',
                text: 'Effettua di nuovo l’accesso.',
            });
            return;
        }

        const cid = (this.form.categoryId ?? '').trim();
        const opt = this.categorySelectOptions.find((o) => o.id === cid);
        const categoryName = opt?.label ?? this.form.categoryName ?? '';

        // Editing (e.g. by the company manager) must not take over the coach's convocation.
        const creatorId = this.isEditMode && this.convocation?.userId ? this.convocation.userId : userId;
        const payload = this.buildApiPayload(creatorId, cid);
        this.saveInProgress = true;
        try {
            if (this.isEditMode && this.convocation?.id) {
                const res = await this.convocationService.updateConvocation(
                    this.convocation.id,
                    payload,
                );
                if (res?.success !== true) {
                    await this.drawerSwal({
                        icon: 'error',
                        title: 'Salvataggio non riuscito',
                        text:
                            res?.errorMessage ||
                            res?.error?.error ||
                            res?.error?.message ||
                            'Riprova più tardi.',
                    });
                    return;
                }
            } else {
                const res = await this.convocationService.createConvocation(
                    payload,
                );
                if (res?.success !== true || !res?.data) {
                    await this.drawerSwal({
                        icon: 'error',
                        title: 'Creazione non riuscita',
                        text:
                            res?.errorMessage ||
                            res?.error?.error ||
                            res?.code ||
                            'Controlla i dati e riprova.',
                    });
                    return;
                }
                if (res.data?.id) {
                    this.form = { ...this.form, id: res.data.id };
                }
            }

            const row: ConvocationRowItem = {
                ...(this.form as ConvocationRowItem),
                id: this.convocation?.id ?? this.form.id ?? '',
                categoryId: cid || undefined,
                categoryName,
                invitedCount: this.invitedUsers.length,
                users: this.invitedUsers.map((u) => u.id),
            };
            this.saved.emit(row);
        } finally {
            this.saveInProgress = false;
        }
    }

    private buildApiPayload(
        userId: string,
        categoryId: string,
    ): ConvocationApiPayload {
        const users = this.invitedUsers.map((u) => u.id).filter(Boolean);
        return {
            title: (this.form.title ?? '').trim(),
            description: (this.form.description ?? '').trim() || '—',
            status: (this.form.status ?? 'Bozza').trim(),
            userId,
            categoryId,
            date: this.toIsoDate(this.form.date),
            location: (this.form.location ?? '').trim(),
            address: (this.form.address ?? '').trim(),
            city: (this.form.city ?? '').trim(),
            province: (this.form.province ?? '').trim() || '—',
            linkPosition: (this.form.linkPosition ?? '').trim() || '—',
            users,
        };
    }

    private normalizeDateTimeForInput(value?: string): string {
        if (!value) {
            return this.todayIso();
        }

        const trimmed = value.trim();
        if (!trimmed) {
            return this.todayIso();
        }

        const localMoment = moment.parseZone(trimmed).local();
        if (localMoment.isValid()) {
            return localMoment.format('YYYY-MM-DDTHH:mm');
        }

        const plainMoment = moment(trimmed);
        if (plainMoment.isValid()) {
            return plainMoment.format('YYYY-MM-DDTHH:mm');
        }

        return this.todayIso();
    }

    private toIsoDate(value?: string): string {
        if (!value) {
            return new Date().toISOString();
        }
        const d = new Date(value);
        return Number.isNaN(d.getTime())
            ? new Date().toISOString()
            : d.toISOString();
    }

    onClose(): void {
        this.closed.emit();
    }

    private generateId(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
        });
    }

    private todayIso(): string {
        const d = new Date();
        d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
        return d.toISOString().slice(0, 16);
    }
}
